import {Box, FormLabel} from '@mui/material';
import AgyleDialog from './agyleDialog';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import config from '../../app/config';
import Typography from '@mui/material/Typography';
import _ from 'lodash';
import API from '../../app/api';
import {setSnackbar} from '../../store/reducers/globals';
import {useDispatch, useSelector} from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import {agylePref} from '../../store/reducers/preferences';
import {diagnosisQualifier} from '../../store/reducers/dicts';
import AgyleDictionaryField from '../ui/selector/agyleDictionaryField';
import Chip from '@mui/material/Chip';
import AgyleButton from '../ui/buttons/agyleButton';
import clsx from 'clsx';
import useOpenState from '../../hooks/useOpenState';
import MessageBox from '../../util/messageBox';

const useStyles = makeStyles((theme) => ({
    titleStyle: {
        fontSize: 18,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: 24,
        fontWeight: 'normal',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    tag: {
        height: '30px',
        padding: '7px 10px 7px 10px',
        borderRadius: '32px',
        fontSize: 12,
        color: '#ACAFB3',
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #ACAFB3',
        cursor: 'pointer',
        minWidth: 'max-content',
        userSelect: 'none',
        '&:hover': {
            color: 'white',
            background: '#5497ec',
            borderColor: '#5497ec',
        },
        '&.selected': {
            color: 'white',
            background: '#2180F7',
            borderColor: '#2180F7',
        },
        '&.disabled': {
            pointerEvents: 'none',
        },
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
}));

const DiagnosisQualifierModal = ({open, onSubmit, cancelDiagnosis, diagId, diagnosis, ecdsVersion}) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // Selectors
    const agylePreference = useSelector(agylePref);
    const dictQualifier = useSelector(diagnosisQualifier);

    // Config
    const diagMaxEnabled = config?.modules?.forms?.ecds?.diagnosisMaxEnabled;

    // Diagnosis max list by snomedCode
    const [diagnosisMaxList, setDiagnosisMaxList] = useState([]);

    // Diagnosis
    const [usedDiagnosis, setUsedDiagnosis] = useState(null);
    const [usedDiagnosisMax, setUsedDiagnosisMax] = useState(null);
    const [usedQualifier, setUsedQualifier] = useState(null);

    const [isConfirmOpen, openConfirm, closeConfirm] = useOpenState(false, true);

    useEffect(() => {
        if (open && !usedDiagnosis) {
            const find = _.find(diagnosis, {id: diagId});
            if (find) {
                setUsedDiagnosis(find);
                setUsedQualifier(find?.qualifier);
                findSubDiagnosis(find?.value?.snomedCode);
            }
        }
    }, [diagnosis, diagId, open]);

    const findSubDiagnosis = (snomedCode) => {
        if (snomedCode && ecdsVersion === 'V4') {
            API.getDiagnosisMax(snomedCode)
                .then(res => {
                    setDiagnosisMaxList(res || []);
                    setUsedDiagnosisMax(_.find(res, {snomedCode}) || null);
                })
                .catch(error => {
                    console.error(error);
                    dispatch(setSnackbar({
                        type: 'error',
                        message: 'There has been a problem with the download of the diagnosis list.',
                    }));
                });
        }
    };

    const handleSubmit = () => {
        closeConfirm();
        onSubmit(diagId, usedDiagnosis, usedDiagnosisMax, usedQualifier);
    };

    const handleCloseDialog = (_e, reason) => {
        if (reason !== 'backdropClick') {
            openConfirm();
        }
    };

    const changeQualifier = (snomedCode) => {
        if (snomedCode !== usedQualifier?.snomedCode) {
            setUsedQualifier(_.find(dictQualifier, {ecdsVersion, snomedCode}));
        }
    };

    const handleCancelDiagnosis = () => {
        closeConfirm();
        cancelDiagnosis(diagId);
    };

    return (
        <>
            <AgyleDialog
                title={t('Choose Diagnosis Qualifier')}
                open={open}
                width="md"
                fullWidth
                disableActions
                darkContentBackground
                closeButtonOnHeader
                onClose={handleCloseDialog}
            >
                <>
                    <div className={classes.container}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {agylePreference?.showSnomedCode && `[${usedDiagnosis?.value?.snomedCode}]`} {usedDiagnosis?.value?.snomedDescription}
                        </Typography>

                        {(diagMaxEnabled && ecdsVersion === 'V4') &&
                            <>
                                <FormLabel>
                                    {t('Subdiagnosis')}
                                </FormLabel>

                                <AgyleDictionaryField
                                    onChange={(_, value) => setUsedDiagnosisMax(value)}
                                    options={diagnosisMaxList}
                                    value={usedDiagnosisMax}
                                    getOptionLabel={(option) => option.snomedFullySpecifiedName}
                                    extended
                                    noBorder={false}
                                    groupBy={opt => opt.group}
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            {agylePreference?.showSnomedCode &&
                                                <Chip
                                                    label={option?.snomedCode || 'UNK'}
                                                    style={{marginRight: 10, minWidth: 150}}
                                                    size="small"
                                                />
                                            }
                                            {option?.snomedFullySpecifiedName}
                                        </Box>
                                    )}
                                    disableClearable
                                />
                            </>
                        }

                        <div className={classes.divRow} style={{marginTop: 10}}>
                            <button
                                className={clsx(classes.tag, usedQualifier?.snomedCode === '415684004' && 'selected')}
                                onClick={changeQualifier.bind(this, '415684004')}
                            >
                                {t('Suspected')}
                            </button>

                            <button
                                className={clsx(classes.tag, usedQualifier?.snomedCode === '410605003' && 'selected')}
                                onClick={changeQualifier.bind(this, '410605003')}
                            >
                                {t('Confirmed')}
                            </button>
                        </div>

                        <AgyleButton
                            onClick={handleSubmit}
                            variant="contained"
                            style={{marginLeft: 'auto', marginRight: 0, marginTop: 6}}
                        >
                            {t('Submit')}
                        </AgyleButton>
                    </div>
                </>
            </AgyleDialog>

            <MessageBox
                open={isConfirmOpen}
                title={t(`Cancel diagnosis`)}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                warning
                onAccept={handleCancelDiagnosis}
                onClose={closeConfirm}
                message={t(`Are you sure you want to cancel this diagnosis?`)}
            />

        </>
    );
};

export default DiagnosisQualifierModal;